import { useEffect, useRef, useState } from 'react'

/** Pending time before the skeleton becomes visible (anti-flash). */
export const SKELETON_DELAY_MS = 150
/** Minimum time a skeleton that became visible stays on screen. */
export const SKELETON_MIN_VISIBLE_MS = 400

/**
 * - `reserve`: pending for less than {@link SKELETON_DELAY_MS} — render the
 *   skeleton with `invisible` so the layout is reserved but nothing flashes
 *   on a fast/cached response.
 * - `skeleton`: the shimmer is visible.
 * - `content`: render the real content (or the empty/error state).
 */
export type SkeletonPhase = 'reserve' | 'skeleton' | 'content'

export interface SkeletonPhaseResult {
  phase: SkeletonPhase
  /**
   * Class for the content wrapper: the 150ms ease-out entry fade, applied
   * only when a skeleton was actually shown — a fast response renders its
   * content directly, without an extra animation.
   */
  contentClassName: string
}

/**
 * Anti-flash loading contract for skeleton loaders (RP-5936):
 * a response faster than {@link SKELETON_DELAY_MS} never shows a skeleton,
 * and a skeleton that did appear stays for {@link SKELETON_MIN_VISIBLE_MS}
 * before the content swap. `isPending` may flip back to `true` (e.g. a
 * dependent query starting) — the phase then returns to the skeleton flow.
 */
export interface SkeletonPhaseOptions {
  /**
   * Start visible instead of reserving. Set when a skeleton is already on
   * screen for this area — the Hub's boot screen paints the tab's
   * placeholders before the sections can mount, so re-running the anti-flash
   * delay here would blank content the viewer is already looking at.
   */
  skipDelay?: boolean
}

export function useSkeletonPhase(
  isPending: boolean,
  options?: SkeletonPhaseOptions,
): SkeletonPhaseResult {
  const startsVisible = isPending && Boolean(options?.skipDelay)
  const [phase, setPhase] = useState<SkeletonPhase>(() => {
    if (!isPending) return 'content'
    return startsVisible ? 'skeleton' : 'reserve'
  })
  const shownAtRef = useRef(startsVisible ? Date.now() : 0)
  const hasShownRef = useRef(startsVisible)

  useEffect(() => {
    if (isPending) {
      if (phase === 'content') {
        // New cycle: forget the previous skeleton so a fast resolution of
        // this cycle renders its content without the entry fade.
        hasShownRef.current = false
        setPhase('reserve')
        return
      }
      if (phase === 'reserve') {
        const timer = window.setTimeout(() => {
          shownAtRef.current = Date.now()
          hasShownRef.current = true
          setPhase('skeleton')
        }, SKELETON_DELAY_MS)
        return () => window.clearTimeout(timer)
      }
      return
    }

    if (phase === 'reserve') {
      setPhase('content')
      return
    }
    if (phase === 'skeleton') {
      const remaining =
        SKELETON_MIN_VISIBLE_MS - (Date.now() - shownAtRef.current)
      if (remaining <= 0) {
        setPhase('content')
        return
      }
      const timer = window.setTimeout(() => setPhase('content'), remaining)
      return () => window.clearTimeout(timer)
    }
  }, [isPending, phase])

  return {
    phase,
    contentClassName:
      phase === 'content' && hasShownRef.current
        ? 'motion-safe:animate-content-enter'
        : '',
  }
}
